/**
 * The venue registry the SPA knows (ADR-0040): WHERE market data comes from and where a Live order would go.
 * A venue is never a run mode — "Paper" and "Backtest" are modes on a venue (lib/runContext.js), so the
 * retired pseudo-venue ids (100 Paper, 102 Backtester) have no row here and must not appear anywhere else.
 *
 * This is the ONE place a venue id is spelled as a number. Every other module asks these helpers
 * (`isCoinbaseAdvanced`, `isCoinbaseSandbox`, `venueName`, …) — the vitest grep gate (venueIdGate.test.js)
 * refuses a raw `=== 2` / `=== 6` comparison outside this file and lib/runContext.js.
 *
 * contract: packages/Utilities/Venue.cs (ids), services/Exchange.Web/Controllers/VaultController.cs
 * (credentials are stored per venue; the Vault UI offers exactly these two).
 */

/** Coinbase Advanced Trade — the real-money venue. */
export const COINBASE = 2;

/** Coinbase Exchange Sandbox — fake funds on a real matching engine. */
export const COINBASE_SANDBOX = 6;

/**
 * The deployable venues, in display order. `code` is the Vault tag the server stores credentials under;
 * `credential` names the key scheme the Vault form collects.
 */
export const VENUES = Object.freeze([
  Object.freeze({
    id: COINBASE,
    name: 'Coinbase Advanced',
    shortName: 'Coinbase',
    code: 'CBA',
    realMoney: true,
    credential: 'coinbase-advanced', // Ed25519 or legacy ECDSA key — no passphrase
  }),
  Object.freeze({
    id: COINBASE_SANDBOX,
    name: 'Coinbase Sandbox',
    shortName: 'Sandbox',
    code: 'CBX',
    realMoney: false,
    credential: 'hmac-passphrase', // key + secret + passphrase
  }),
]);

/** Coerce a wire/form venue id (number or numeric string) to a number, or null when it is not one. */
export function toVenueId(raw) {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isInteger(n) ? n : null;
}

/** The registry row for a venue id, or null for an id the SPA does not know (never a guess). */
export function venueById(id) {
  const n = toVenueId(id);
  return n === null ? null : (VENUES.find((v) => v.id === n) ?? null);
}

/** True for a venue the SPA can deploy to or store credentials for. */
export function isKnownVenue(id) {
  return venueById(id) !== null;
}

/** True for the real-money venue (Coinbase Advanced). */
export function isCoinbaseAdvanced(id) {
  return venueById(id)?.id === COINBASE;
}

/** True for the Coinbase Exchange sandbox venue. */
export function isCoinbaseSandbox(id) {
  return venueById(id)?.id === COINBASE_SANDBOX;
}

/** Whether a venue's credential scheme needs a passphrase (the sandbox's HMAC keys do; Advanced Trade's do not). */
export function requiresPassphrase(id) {
  return venueById(id)?.credential === 'hmac-passphrase';
}

/** A display name for any venue id — a known row's name, or a neutral fallback for a legacy row. */
export function venueName(id) {
  const row = venueById(id);
  if (row) return row.name;
  const n = toVenueId(id);
  return n === null ? 'Unknown venue' : `Venue ${n}`;
}
